import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "shards-react";
import { toast } from 'react-toastify';
import httpClient from '../../core/http-common';
import authStore, { types as authTypes, actions as authActions } from '../../core/auth.store';
import SESSION_CONFIG from '../../core/config/sessionConfig';
import './SessionTimeoutWarning.css';

// Longer timeout for refresh so slow server does not trigger logout
const REFRESH_TIMEOUT_MS = 30000;

const SessionTimeoutWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const TOKEN_EXPIRY_MS = SESSION_CONFIG.TOKEN_EXPIRY_MS;
  const WARNING_TIME_MS = SESSION_CONFIG.WARNING_TIME_MS;
  const CHECK_INTERVAL_MS = SESSION_CONFIG.CHECK_INTERVAL_MS;
  const AUTO_REFRESH_THRESHOLD_MS = SESSION_CONFIG.AUTO_REFRESH_THRESHOLD_MS;

  const getTokenExpiryTime = useCallback(() => {
    const loginTime = localStorage.getItem('loginTime');
    if (loginTime) {
      return parseInt(loginTime) + TOKEN_EXPIRY_MS;
    }
    return null;
  }, [TOKEN_EXPIRY_MS]);

  const refreshToken = async () => {
    setIsRefreshing(true);
    try {
      const response = await httpClient.post('/api/v2/users/refresh-token', {}, {
        withCredentials: true,
        timeout: REFRESH_TIMEOUT_MS,
      });

      if (response.data.success) {
        localStorage.setItem('loginTime', Date.now().toString());
        setShowWarning(false);
        setTimeRemaining(0);
        if (process.env.NODE_ENV === 'development') console.info('Token refreshed successfully');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to refresh token:', error);
      const isUnauthorized = error.response && error.response.status === 401;
      const isTimeoutOrNetwork = !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error');

      if (isUnauthorized) {
        try {
          if (authActions.removeAuthStorage) authActions.removeAuthStorage();
          authStore.dispatch({ type: authTypes.INIT_AUTH_STATE });
        } catch (e) {}
        toast.info("Session expired. Redirecting to login.", { autoClose: 2000 });
        setTimeout(handleLogout, 500);
      } else if (isTimeoutOrNetwork) {
        toast.warning("Request timed out. Please try again.", { autoClose: 4000 });
      } else {
        toast.warning("Could not refresh session. Please try again or log in again.", { autoClose: 4000 });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('loginTime');
    const subdomain = process.env.REACT_APP_SUB_DOMAIN || '';
    window.location.href = subdomain + '/login';
  };

  const checkSessionExpiry = useCallback(() => {
    const expiryTime = getTokenExpiryTime();
    if (!expiryTime) return;

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    if (timeUntilExpiry <= 0) {
      handleLogout();
      return;
    }

    const warningThreshold = TOKEN_EXPIRY_MS - WARNING_TIME_MS;
    if (timeUntilExpiry <= warningThreshold) {
      setShowWarning(true);
      setTimeRemaining(Math.ceil(timeUntilExpiry / 1000));
    } else {
      setShowWarning(false);
      setTimeRemaining(0);
    }
  }, [getTokenExpiryTime, TOKEN_EXPIRY_MS, WARNING_TIME_MS]);

  useEffect(() => {
    checkSessionExpiry();
    const intervalId = setInterval(checkSessionExpiry, CHECK_INTERVAL_MS);

    let countdownInterval;
    if (showWarning && timeRemaining > 0) {
      countdownInterval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(intervalId);
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [showWarning, timeRemaining, checkSessionExpiry, CHECK_INTERVAL_MS]);

  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    let lastActivity = Date.now();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > 10 * 60 * 1000) {
        const expiryTime = getTokenExpiryTime();
        if (expiryTime) {
          const timeUntilExpiry = expiryTime - now;
          if (timeUntilExpiry < AUTO_REFRESH_THRESHOLD_MS && timeUntilExpiry > 0) {
            refreshToken();
          }
        }
      }
      lastActivity = now;
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [getTokenExpiryTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showWarning) return null;

  return (
    <Modal
      open={showWarning}
      toggle={() => {}}
      className="session-timeout-modal"
    >
      <ModalHeader>
        <span role="img" aria-label="Warning">⚠️</span> Session Expiring Soon
      </ModalHeader>
      <ModalBody>
        <p>Your session will expire in <strong>{formatTime(timeRemaining)}</strong></p>
        <p>Would you like to continue working?</p>
      </ModalBody>
      <ModalFooter>
        <Button theme="secondary" onClick={handleLogout} disabled={isRefreshing}>
          Logout
        </Button>
        <Button theme="primary" onClick={refreshToken} disabled={isRefreshing}>
          {isRefreshing ? "Refreshing…" : "Continue Session"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SessionTimeoutWarning;
